import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { OrderStatusConfirmComponent } from '../components/order-status-confirm/order-status-confirm.component';
import { DialogAction, DialogCheckbox, DialogProps, DialogStatus } from '../components/order-status-confirm/order-status-confirm.types';
import { IErpMissingInventory, IErpMissingProduct } from '../models/quotations.models';

/**
 * Builds and opens the order status-transition modal
 * (`OrderStatusConfirmComponent`) for the transitions that more than one
 * surface needs.
 *
 * Extracted from `DetailsQuotesComponent.confirmAction` (WB-7478) so the
 * side-by-side correction panel can drive the same modals instead of
 * duplicating ~150 lines of dialog config. Callers own the domain effect and
 * pass it as `onConfirm`; the service owns the copy, the status chips, the
 * button classes and closing the dialog.
 *
 * The ERP-conflict transitions (`erp_missing_products`, `erp_missing_inventory`,
 * `force_send_conflict`) live here too: the side-by-side panel now runs the same
 * approval flow, so both surfaces must present identical follow-ups when the ERP
 * rejects lines.
 *
 * The remaining quote-lifecycle transitions (`draft`, `requested`, `sent`,
 * `send_accepted_order`) intentionally stay in `DetailsQuotesComponent`: they are
 * order-details-only and read a lot of host state, so extracting them would widen
 * the diff with no second consumer.
 */
@Injectable({ providedIn: 'root' })
export class OrderStatusDialogService {
  constructor(
    private dialog: MatDialog,
    private translateService: TranslateService
  ) {}

  /** Thin wrapper so every caller gets the same dialog wiring. */
  open(data: DialogProps): MatDialogRef<OrderStatusConfirmComponent> {
    return this.openScoped(() => data);
  }

  /**
   * Opens the confirm dialog and hands the builder a `close` bound to *this* dialog's
   * own ref.
   *
   * Critically NOT `MatDialog.closeAll()`, which every action used to call: that tears
   * down every open dialog, including the side-by-side panel hosting the confirm. The
   * panel was therefore destroyed before its own `onConfirm` effects ran — its
   * `takeUntil(destroy$)` subscriptions were gone, so the approval outcome (ERP
   * conflicts, refetch, close-with-result) was silently dropped.
   *
   * `build` runs before `ref` is assigned, which is safe: `close` is only ever invoked
   * later, from a button callback.
   */
  private openScoped(
    build: (close: () => void) => DialogProps
  ): MatDialogRef<OrderStatusConfirmComponent> {
    let ref: MatDialogRef<OrderStatusConfirmComponent> | undefined;
    const close = () => ref?.close();

    ref = this.dialog.open(OrderStatusConfirmComponent, {
      data: {
        misProducts: [],
        misInventory: [],
        erp_description: '',
        mainIcon: '',
        status: {},
        ...build(close),
      },
    });
    return ref;
  }

  /**
   * Approve an order: `to_be_validated` / `pending_inventory` → `done`.
   *
   * `fromStatus` drives the left-hand status chip so the modal reflects the
   * record's real starting point.
   *
   * `checkbox` is optional because the preference row it carries is surface-specific:
   * order details offers "return to the order list after confirmation" (WB-7247),
   * while the Smart Inbox panel has no list to return to. Callers own that state, so
   * they build the row; the service only renders what it is handed.
   */
  openApproveOrder(options: {
    fromStatus: 'to_be_validated' | 'pending_inventory';
    onConfirm: () => void;
    checkbox?: DialogCheckbox;
  }): MatDialogRef<OrderStatusConfirmComponent> {
    return this.openScoped((close) => ({
      title: this.t('quotations_and_orders.status_dialog.approve_order.title'),
      description: this.t('quotations_and_orders.status_dialog.approve_order.description'),
      status: {
        from: {
          text: this.t('quotations_and_orders.QuotationStatus.' + options.fromStatus),
          class: options.fromStatus === 'pending_inventory' ? 'status-pending' : 'status-primary',
        },
        to: {
          text: this.t('quotations_and_orders.QuotationStatus.done'),
          class: 'status-success',
        },
      },
      checkbox: options.checkbox,
      actions: [
        this.dangerCancelAction(close),
        this.forwardAction(
          'quotations_and_orders.status_dialog.actions.approve_order',
          close,
          options.onConfirm
        ),
      ],
    }));
  }

  /** Cancel an order (destructive, no status chips — icon-led). */
  openCancelOrder(options: { onConfirm: () => void }): MatDialogRef<OrderStatusConfirmComponent> {
    return this.openScoped((close) => ({
      title: this.t('quotations_and_orders.status_dialog.cancel_order.title'),
      description: this.t('quotations_and_orders.status_dialog.cancel_order.description'),
      mainIcon: 'assets/icones/main_cancel_icon.svg',
      actions: [
        this.goBackAction(close),
        {
          label: this.t('quotations_and_orders.status_dialog.actions.cancel_order'),
          class: 'action-cancel',
          icon: { position: 'before', name: 'assets/icones/cancel_icon_white.svg' },
          callback: () => this.run(close, options.onConfirm),
        },
      ],
    }));
  }

  /** Delete an order (destructive). */
  openDeleteOrder(options: { onConfirm: () => void }): MatDialogRef<OrderStatusConfirmComponent> {
    return this.openScoped((close) => ({
      title: this.t('quotations_and_orders.status_dialog.delete_order.title'),
      description: this.t('quotations_and_orders.status_dialog.delete_order.description'),
      mainIcon: 'assets/icones/main_delete_icon.svg',
      actions: [
        this.goBackAction(close),
        {
          label: this.t('quotations_and_orders.status_dialog.actions.delete_order'),
          class: 'action-cancel',
          icon: { position: 'before', name: 'assets/icones/cancel_icon_white.svg' },
          callback: () => this.run(close, options.onConfirm),
        },
      ],
    }));
  }

  openDuplicateOrder(options: {
    name?: string;
    onConfirm: () => void;
  }): MatDialogRef<OrderStatusConfirmComponent> {
    return this.openScoped((close) => ({
      title: this.t('quotations_and_orders.confirm.duplicate_title'),
      description: this.t('quotations_and_orders.confirm.duplicate_body', { name: options.name ?? '' }),
      mainIcon: 'assets/icones/copy.svg',
      actions: [
        this.goBackAction(close),
        this.forwardAction('quotations_and_orders.actions.duplicate', close, options.onConfirm),
      ],
    }));
  }

  openArchiveOrder(options: {
    name?: string;
    onConfirm: () => void;
  }): MatDialogRef<OrderStatusConfirmComponent> {
    return this.openScoped((close) => ({
      title: this.t('quotations_and_orders.confirm.archive_title'),
      description: this.t('quotations_and_orders.confirm.archive_body', { name: options.name ?? '' }),
      mainIcon: 'assets/icones/archive.svg',
      actions: [
        this.goBackAction(close),
        this.forwardAction('quotations_and_orders.actions.archive', close, options.onConfirm),
      ],
    }));
  }

  openUnarchiveOrder(options: {
    name?: string;
    onConfirm: () => void;
  }): MatDialogRef<OrderStatusConfirmComponent> {
    return this.openScoped((close) => ({
      title: this.t('quotations_and_orders.confirm.unarchive_title'),
      description: this.t('quotations_and_orders.confirm.unarchive_body', { name: options.name ?? '' }),
      mainIcon: 'assets/icones/unarchive.svg',
      actions: [
        this.goBackAction(close),
        this.forwardAction('quotations_and_orders.actions.unarchive', close, options.onConfirm),
      ],
    }));
  }

  // ── ERP conflict follow-ups (post-approve) ─────────────────────────────────

  /**
   * The ERP could not find some products. Offers to drop them and retry, unless
   * *every* line is missing — there would be nothing left to send.
   */
  openErpMissingProducts(options: {
    missingProducts: IErpMissingProduct[];
    allProductsMissing: boolean;
    onRemoveProducts: () => void;
  }): MatDialogRef<OrderStatusConfirmComponent> {
    return this.openScoped((close) => ({
      title: this.t('quotations_and_orders.status_dialog.erp_missing_products.title'),
      description: this.t('quotations_and_orders.status_dialog.erp_missing_products.description'),
      status: this.toPendingInventory(),
      misProducts: options.missingProducts ?? [],
      erp_description: this.t('quotations_and_orders.status_dialog.erp_missing_products.erp_description'),
      actions: [
        ...(options.allProductsMissing ? [] : [{
          label: this.t('quotations_and_orders.status_dialog.actions.remove_products'),
          class: 'action-danger',
          icon: { position: 'before' as const, name: 'assets/icones/cancel_icon.svg' },
          callback: () => this.run(close, options.onRemoveProducts),
        }]),
        {
          label: this.t('quotations_and_orders.status_dialog.actions.ok'),
          class: 'action-primary',
          callback: () => close(),
        },
      ],
    }));
  }

  /** Not enough stock. Either leave the order pending, or force it through. */
  openErpMissingInventory(options: {
    missingInventory: IErpMissingInventory[];
    onForceApprove: () => void;
  }): MatDialogRef<OrderStatusConfirmComponent> {
    return this.openScoped((close) => ({
      title: this.t('quotations_and_orders.status_dialog.erp_missing_inventory.title'),
      description: this.t('quotations_and_orders.status_dialog.erp_missing_inventory.description'),
      status: this.toPendingInventory(),
      misInventory: options.missingInventory ?? [],
      erp_description: this.t('quotations_and_orders.status_dialog.erp_missing_inventory.erp_description'),
      actions: [
        {
          label: this.t('quotations_and_orders.status_dialog.actions.keep_pending'),
          class: 'action-light',
          icon: { position: 'after', name: 'assets/icones/time_icon.svg' },
          callback: () => close(),
        },
        {
          label: this.t('quotations_and_orders.status_dialog.actions.force_approve'),
          class: 'action-primary',
          icon: { position: 'after', name: 'arrow_forward' },
          callback: () => this.run(close, options.onForceApprove),
        },
      ],
    }));
  }

  /**
   * The requested delivery date conflicts with ERP availability. Offers to send
   * anyway on the original date.
   */
  openForceSendConflict(options: {
    missingInventory: IErpMissingInventory[];
    onKeepOriginalDate: () => void;
  }): MatDialogRef<OrderStatusConfirmComponent> {
    return this.openScoped((close) => ({
      title: this.t('quotations_and_orders.status_dialog.force_send_conflict.title'),
      description: this.t('quotations_and_orders.status_dialog.force_send_conflict.description'),
      status: {
        from: {
          text: this.t('quotations_and_orders.QuotationStatus.to_be_validated'),
          class: 'status-primary',
        },
        to: {
          text: this.t('quotations_and_orders.QuotationStatus.done'),
          class: 'status-success',
        },
      },
      misInventory: options.missingInventory ?? [],
      erp_description: this.t('quotations_and_orders.status_dialog.force_send_conflict.erp_description'),
      actions: [
        this.dangerCancelAction(close),
        this.forwardAction(
          'quotations_and_orders.status_dialog.actions.keep_original_date',
          close,
          options.onKeepOriginalDate
        ),
      ],
    }));
  }

  /** Shared `to_be_validated → pending_inventory` chip pair. */
  private toPendingInventory(): { from: DialogStatus; to: DialogStatus } {
    return {
      from: {
        text: this.t('quotations_and_orders.QuotationStatus.to_be_validated'),
        class: 'status-primary',
      },
      to: {
        text: this.t('quotations_and_orders.QuotationStatus.pending_inventory'),
        class: 'status-pending',
      },
    };
  }

  // ── Shared action builders ─────────────────────────────────────────────────

  /** Dismiss-only button styled as danger (used by the status transitions). */
  private dangerCancelAction(close: () => void): DialogAction {
    return {
      label: this.t('quotations_and_orders.status_dialog.actions.cancel'),
      class: 'action-danger',
      callback: () => close(),
    };
  }

  /** Dismiss-only button styled as light (used by the destructive confirms). */
  private goBackAction(close: () => void): DialogAction {
    return {
      label: this.t('quotations_and_orders.status_dialog.actions.go_back'),
      class: 'action-light',
      callback: () => close(),
    };
  }

  /** Primary confirm button with the trailing arrow. */
  private forwardAction(labelKey: string, close: () => void, onConfirm: () => void): DialogAction {
    return {
      label: this.t(labelKey),
      class: 'action-primary',
      icon: { position: 'after', name: 'arrow_forward' },
      callback: () => this.run(close, onConfirm),
    };
  }

  /**
   * Dismisses this dialog, then runs the caller's effect.
   *
   * Close-first is deliberate: some effects open a follow-up dialog (approve on a Nava
   * tenant routes into `checkQuotationAvailability`, which can open the
   * products-availability dialog), and closing afterwards would dismiss that too.
   */
  private run(close: () => void, onConfirm: () => void): void {
    close();
    onConfirm();
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(key, params);
  }
}

/** Re-exported for hosts that build ad-hoc dialogs alongside the presets. */
export type { DialogAction, DialogProps, DialogStatus };
